import React, { useEffect, useRef, useState } from 'react';
import { Link } from '../root/components/Link';
import { SignedIn, useSignIn } from '@clerk/clerk-react';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { PhoneOtcValidation } from './PhoneOtcValidation';
import { MagicLinkValidation } from './MagicLinkValidation';
import { CardBody } from '../components/CardBody';
import { CardFooter } from '../components/CardFooter';
import { LazyImageLoader } from '../product_detail/components/LazyImageLoader';
import { useRecoilValue } from 'recoil';
import { assetPathFnState } from '../recoil/atoms';
import styles from './LoyaltyForm.module.scss';
import { Button } from '../root/components/buttons/Button';

declare const window: HoneyloveWindow;

enum SignInSteps {
  EmailOrPhoneEntryStep,
  OneTimeCodeStep,
  WaitingForMagicLinkVerification,
}

const SignInApp: React.FC = () => {
  const assetPathFn = useRecoilValue(assetPathFnState);
  const [emailOrPhone, setEmailOrPhone] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [step, setStep] = useState<SignInSteps>(SignInSteps.EmailOrPhoneEntryStep);
  const emailOrPhoneRef = useRef<HTMLInputElement>(null);

  const { signIn, isLoaded } = useSignIn();

  const startOver = async (err?: string) => {
    setError(err || '');
    setStep(SignInSteps.EmailOrPhoneEntryStep);
  };

  const submitSignIn = async () => {
    setError('');
    const isEmail = emailOrPhone.includes('@');

    if (isEmail) {
      // If it is email, do Magic Link
      setStep(SignInSteps.WaitingForMagicLinkVerification);
    } else {
      // If it is phone, do OTC
      setStep(SignInSteps.OneTimeCodeStep);
    }
  };

  return (
    <main role="main" className={styles.pageWrapper}>
      <SignedIn>
        <LoggedInRedirect />
      </SignedIn>
      <CardBody>
        <div className={styles.hiveForm}>
          <div className={styles.formHeader}>
            <h1>Welcome back.</h1>
            <div className={styles.controls}>
              {step === SignInSteps.EmailOrPhoneEntryStep && (
                <>
                  <div className={styles.active}>Already a member?</div>
                  <div>
                    <Link dataTrackLabel="Already a member?" dataTrackLocation="SignInApp" href="/pages/sign-up" size="large">
                      Sign up - it&apos;s free!
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={styles.formWrapper}>
            <div className={styles.formBody}>
              {step === SignInSteps.EmailOrPhoneEntryStep && (
                <>
                  <input
                    ref={emailOrPhoneRef}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    type="text"
                    placeholder={'Email or phone'}
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && submitSignIn()}
                    pattern="^(\d{10})$|^(.+@[\w\.\-]+(\.\w+)+)$"
                  />
                  <Button
                    text="Sign in"
                    onClick={() => submitSignIn()}
                    dataTrackLabel="Sign in"
                    dataTrackLocation="Loyalty sign in page"
                    disabled={!emailOrPhoneRef?.current?.value || !emailOrPhoneRef?.current?.validity?.valid}
                    fullWidth={true}
                    size="large"
                  />
                  {error && <div className={styles.error}>{error}</div>}
                </>
              )}
              {isLoaded && step === SignInSteps.OneTimeCodeStep && (
                <PhoneOtcValidation clerkResource={{ type: 'signin', signIn }} phone={emailOrPhone} startOver={startOver} />
              )}
              {isLoaded && step === SignInSteps.WaitingForMagicLinkVerification && (
                <MagicLinkValidation clerkResource={{ type: 'signin', signIn }} emailAddress={emailOrPhone} startOver={startOver} phoneNumber={undefined} />
              )}
            </div>
            <div className={styles.hiveModels}>
              <LazyImageLoader src={assetPathFn('hive-models.jpg')} alt="Honeylove hive models" />
            </div>
          </div>
        </div>
      </CardBody>
      <CardFooter />
    </main>
  );
};

const LoggedInRedirect = () => {
  useEffect(() => {
    window.location.href = '/pages/profile';
  }, []);
  return <></>;
};

export default SignInApp;
